import firebase from 'firebase/app';
import { observer, useLocalStore } from 'mobx-react-lite';
import React, { useContext, useEffect } from 'react';
import { UsersContext } from '../../contexts/Users';
import { db } from '../../firebase';
import AutosaveAnswer from './AutosaveAnswer';
import * as CSS from './elements/StudentAssessment';

const SAVE_STATUS_TEXT = {
  idle: 'Your answers save automatically',
  saving: 'Saving…',
  saved: 'All changes saved',
  error: "Couldn't save your answer. Check your connection and try again.",
};

const StudentAssessment = observer(({ questionSet }) => {
  const validQuestionSets = {
    start: 'initialQuestions',
    end: 'finalQuestions',
  };
  const validQuestionPrefixes = {
    start: 'initial',
    end: 'final',
  };
  const validQuestionSet = validQuestionSets[questionSet];
  const validQuestionPrefix = validQuestionPrefixes[questionSet];

  const usersStore = useContext(UsersContext);
  const loggedInUser = usersStore.getLoggedInUser();
  const store = useLocalStore(() => ({
    saveStatus: 'idle',
    setSaveStatus(status) {
      store.saveStatus = status;
    },
  }));

  const assessment = loggedInUser.private.getActiveAssessmentByCategory(
    loggedInUser.selectedCategory
  );

  useEffect(() => {
    if (assessment && validQuestionSet) {
      db.collection('usersPrivate')
        .doc(usersStore.loggedInUserId)
        .update({
          [`learningAssessments.${loggedInUser.selectedCategory}.${validQuestionPrefix}LastChecked.${usersStore.loggedInUserId}`]: firebase.firestore.Timestamp.fromDate(
            new Date()
          ),
        });
    }
  }, [
    assessment,
    loggedInUser.selectedCategory,
    usersStore.loggedInUserId,
    validQuestionPrefix,
    validQuestionSet,
  ]);

  if (!assessment || !validQuestionSet)
    return (
      <div data-testid="StudentAssessment">
        <CSS.CardWithImage isAssessment>
          <CSS.InfoHeader data-testid="StudentAssessment-none">
            No learning plan yet
          </CSS.InfoHeader>
          <CSS.NoAssessmentIcon />
        </CSS.CardWithImage>
      </div>
    );

  const questions = assessment
    .getOrderedQuestions(validQuestionSet)
    .map((question) => question.toJSON());

  const startUncheckedCount = assessment.getUncheckedCount(
    'initial',
    usersStore.loggedInUserId
  );
  const endUncheckedCount = assessment.getUncheckedCount(
    'final',
    usersStore.loggedInUserId
  );

  const assessmentPath = `learningAssessments.${loggedInUser.selectedCategory}`;

  // each answer is saved on its own, so one save can't overwrite another answer
  // (or a teacher's edit) made in the meantime
  const saveAnswer = (questionId, answer) => {
    const now = firebase.firestore.Timestamp.fromDate(new Date());
    return db
      .collection('usersPrivate')
      .doc(usersStore.loggedInUserId)
      .update({
        [`${assessmentPath}.${validQuestionSet}.${questionId}.answer`]: answer,
        [`${assessmentPath}.${validQuestionPrefix}LastModified`]: now,
        [`${assessmentPath}.${validQuestionPrefix}LastChecked.${usersStore.loggedInUserId}`]: now,
      });
  };

  return (
    <div data-testid="StudentAssessment">
      <CSS.AssessmentForm
        data-testid="StudentAssessment-form"
        onSubmit={(e) => e.preventDefault()}
      >
        <CSS.AssessmentHeader>My Learning Plan</CSS.AssessmentHeader>
        <CSS.StaffTabs>
          <CSS.UnreadCount
            badgeContent={startUncheckedCount}
            data-testid={`unread-count-studentStart`}
          >
            <CSS.AssessmentLink to={`/learningPlan/start`}>
              Start
            </CSS.AssessmentLink>
          </CSS.UnreadCount>
          <CSS.UnreadCount
            badgeContent={endUncheckedCount}
            data-testid={`unread-count-studentEnd`}
          >
            <CSS.AssessmentLink to={`/learningPlan/end`}>
              End
            </CSS.AssessmentLink>
          </CSS.UnreadCount>
        </CSS.StaffTabs>
        <CSS.AnswerList>
          {questions.map((question, i) => (
            <AutosaveAnswer
              key={question.id}
              question={question}
              index={i}
              onSave={saveAnswer}
              onStatusChange={store.setSaveStatus}
            />
          ))}
        </CSS.AnswerList>
        <CSS.SaveStatus
          role="status"
          data-testid="StudentAssessment-save-status"
          $isError={store.saveStatus === 'error'}
        >
          {SAVE_STATUS_TEXT[store.saveStatus]}
        </CSS.SaveStatus>
      </CSS.AssessmentForm>
    </div>
  );
});

export default StudentAssessment;
